import { hasViewStateInUrl } from './viewStateDeepLink';

export const SHARED_VIEW_PARAM = 'sharedView';
const SHARED_VIEW_SESSION_KEY = 'roadmap_shared_view_session';

/** Tracks whether URL view state has been applied this browser session (avoids re-applying on SPA route changes). */
let sharedViewUrlStateApplied = false;

/** View-state query params that must survive slideout / route transitions in shared view. */
export const VIEW_STATE_QUERY_PARAMS = [
  SHARED_VIEW_PARAM,
  'startDate',
  'endDate',
  'products',
  'productGroups',
  'categories',
  'audiences',
  'events',
  'features',
  'status',
  'tiers',
  'genAi',
  'revenueImpact',
  'momentTypes',
  'categoryProductCategories',
  'categoryProductProducts',
  'surfaces',
  'compact',
  'parkingLot',
  'collapseAll',
  'unNestAll',
  'sortColumn',
  'sortOption',
  'pinColumn',
  'pinOption',
  'mustWin',
  'mustWinType',
  'mustWinValue',
  'mustWinLabel',
  'mustWinFilters',
  'search',
  'issueId',
  'metaCollapsed',
  'betaView',
  'timelineView',
  'timelineIcons'
] as const;

/** Params tied to a specific slideout — drop when changing top-level routes. */
export const SLIDEOUT_ONLY_QUERY_PARAMS = [
  'phaseId',
  'moment',
  'card',
  'featureId',
  'productId'
] as const;

function normalizeSearch(search: string): string {
  return search.startsWith('?') ? search.slice(1) : search;
}

export function isSharedViewInSearch(search: string): boolean {
  const params = new URLSearchParams(normalizeSearch(search));
  return params.get(SHARED_VIEW_PARAM) === 'true';
}

/**
 * True when the current tab is in shared-view mode (URL flag or active session).
 * Session keeps shared mode across in-app navigation when the flag is preserved in the URL.
 */
export function isSharedViewMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (isSharedViewInSearch(window.location.search)) {
    return true;
  }

  try {
    return sessionStorage.getItem(SHARED_VIEW_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Shared view detected from URL only — used on full page load / refresh. */
export function isSharedViewPageLoad(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return isSharedViewInSearch(window.location.search);
}

/**
 * Tracks whether a real, user-created Saved View (not the built-in My Stuff
 * view) is currently active. Set by useSavedViewActions whenever
 * activeSavedViewId changes, so ordinary filter-change persistence
 * (saveUserPreferences) automatically stops writing into the shared
 * Roadmap/My Stuff preferences blob while a Saved View's own filters are
 * being viewed/tweaked — mirrors the existing sharedViewUrlStateApplied
 * module-flag pattern in this file.
 */
let isRealSavedViewActive = false;

export function markSavedViewActive(active: boolean): void {
  isRealSavedViewActive = active;
}

export function shouldSkipPreferencePersistence(): boolean {
  return isSharedViewMode() || isRealSavedViewActive;
}

/** Skip restoring filter/view preferences from localStorage when URL owns the view state. */
export function shouldPreferUrlOverLocalPreferences(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return isSharedViewPageLoad() || hasViewStateInUrl();
}

export function syncSharedViewSessionFromSearch(search: string): boolean {
  const active = isSharedViewInSearch(search);

  if (typeof window === 'undefined') {
    return active;
  }

  try {
    if (active) {
      sessionStorage.setItem(SHARED_VIEW_SESSION_KEY, 'true');
    } else {
      sessionStorage.removeItem(SHARED_VIEW_SESSION_KEY);
      resetSharedViewUrlStateApplied();
    }
  } catch {
    // Ignore sessionStorage failures (private browsing, etc.)
  }

  return active;
}

export function clearSharedViewSession(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(SHARED_VIEW_SESSION_KEY);
  } catch {
    // Ignore sessionStorage failures
  }

  resetSharedViewUrlStateApplied();
}

export function markSharedViewUrlStateApplied(): void {
  sharedViewUrlStateApplied = true;
}

export function hasSharedViewUrlStateBeenApplied(): boolean {
  return sharedViewUrlStateApplied;
}

export function resetSharedViewUrlStateApplied(): void {
  sharedViewUrlStateApplied = false;
}

/**
 * Apply URL view state only on the first load of a shared/deep-link tab.
 * Skip on subsequent SPA route changes — Redux already holds the live selection.
 */
export function shouldApplyViewStateFromUrlOnMount(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (sharedViewUrlStateApplied) {
    return false;
  }

  return shouldPreferUrlOverLocalPreferences();
}

export function appendSharedViewFlagToUrl(url: string): string {
  const urlObj = new URL(url);
  urlObj.searchParams.set(SHARED_VIEW_PARAM, 'true');
  return urlObj.toString();
}

export function preserveSharedViewFlagInUrl(url: string): string {
  if (!isSharedViewMode()) {
    return url;
  }
  return appendSharedViewFlagToUrl(url);
}

/** Merge view-state params from the current location into a target search string. */
export function mergeViewStateSearchParams(
  currentSearch: string,
  targetSearch?: string
): URLSearchParams {
  const merged = new URLSearchParams(
    normalizeSearch(targetSearch ?? currentSearch)
  );
  const currentParams = new URLSearchParams(normalizeSearch(currentSearch));

  VIEW_STATE_QUERY_PARAMS.forEach(param => {
    const value = currentParams.get(param);
    if (value !== null && !merged.has(param)) {
      merged.set(param, value);
    }
  });

  if (isSharedViewMode() && !merged.has(SHARED_VIEW_PARAM)) {
    merged.set(SHARED_VIEW_PARAM, 'true');
  }

  return merged;
}

/**
 * Build a route path that keeps shared-view query params (including sharedView=true)
 * when navigating between top-level pages such as Roadmap and Milestones.
 */
export function buildPathWithPreservedViewState(
  targetPathname: string,
  currentSearch: string
): string {
  const normalizedPath = targetPathname.replace(/\/+$/, '') || '/';

  if (!isSharedViewMode() && !isSharedViewInSearch(currentSearch)) {
    return normalizedPath;
  }

  const params = mergeViewStateSearchParams(currentSearch, '');
  SLIDEOUT_ONLY_QUERY_PARAMS.forEach(key => params.delete(key));

  const query = params.toString();
  return query ? `${normalizedPath}?${query}` : normalizedPath;
}

/**
 * Preserve query params when closing slideout or switching routes.
 * Uses shared-view rules when in shared-view mode; otherwise keeps existing params.
 */
export function buildPathPreservingQueryParams(
  targetPathname: string,
  currentSearch: string
): string {
  if (isSharedViewMode() || isSharedViewInSearch(currentSearch)) {
    return buildPathWithPreservedViewState(targetPathname, currentSearch);
  }

  const normalizedPath = targetPathname.replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(normalizeSearch(currentSearch));
  SLIDEOUT_ONLY_QUERY_PARAMS.forEach(key => params.delete(key));
  const query = params.toString();
  return query ? `${normalizedPath}?${query}` : normalizedPath;
}
